(() => {
  if (customElements.get('contact-popup')) return;

  const TOAST_DURATION_MS = 3000;
  const TOAST_EXIT_MS = 300;
  const PENDING_SUBMIT_KEY = 'contact-popup-pending-submit';
  const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
  const CONTACT_POSTED_PARAM = 'contact_posted';

  class ContactPopup extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
      this.captchaState = 'idle';
      this.pendingCaptchaSubmit = false;
      this.isCaptchaResubmit = false;
    }

    connectedCallback() {
      this.form = this.querySelector('form.contact-popup__form');
      this.toastEl = this.querySelector('[data-toast]');
      this.toastTextEl = this.querySelector('[data-toast-text]');
      this.toastHideTimer = null;
      this.toastRemoveTimer = null;

      if (this.toastEl) {
        this.portalToast();
      }

      this.querySelectorAll('[data-action="close"]').forEach((el) =>
        el.addEventListener('click', this.onClose)
      );

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit);
        this.submitButton = this.form.querySelector('.contact-popup__submit');
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmitButtonClick, true);
        }
      }

      document.addEventListener('click', this.onTriggerClick);
      document.addEventListener('keydown', this.onKeyDown);

      this.initShopifyCaptcha();
      this.cleanupStalePendingSubmit();
      this.handlePostSubmitState();
    }

    disconnectedCallback() {
      this.clearToastTimers();

      this.querySelectorAll('[data-action="close"]').forEach((el) =>
        el.removeEventListener('click', this.onClose)
      );

      if (this.form) {
        this.form.removeEventListener('submit', this.onSubmit);
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.onSubmitButtonClick, true);
        }
      }

      document.removeEventListener('click', this.onTriggerClick);
      document.removeEventListener('keydown', this.onKeyDown);

      if (this.isOpen()) document.body.style.overflow = '';
    }

    portalToast() {
      if (!this.toastEl) return;

      if (this.toastEl.parentElement !== document.body) {
        document.body.appendChild(this.toastEl);
      }
    }

    isOpen() {
      return this.getAttribute('aria-hidden') === 'false';
    }

    getReturnPath() {
      return window.location.pathname;
    }

    isOnReturnPage(pending) {
      return Boolean(pending?.returnPath && pending.returnPath === this.getReturnPath());
    }

    hasFreshPendingSubmit() {
      const pending = this.readPendingSubmit();
      return Boolean(pending && this.isOnReturnPage(pending));
    }

    hasContactPostedParam() {
      return new URLSearchParams(window.location.search).get(CONTACT_POSTED_PARAM) === 'true';
    }

    returnedFromChallenge() {
      try {
        return document.referrer.includes('/challenge');
      } catch {
        return false;
      }
    }

    readPendingSubmit() {
      try {
        const raw = sessionStorage.getItem(PENDING_SUBMIT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch {
        return null;
      }
    }

    writePendingSubmit(data) {
      try {
        sessionStorage.setItem(PENDING_SUBMIT_KEY, JSON.stringify(data));
      } catch {
        /* ignore storage errors */
      }
    }

    clearPendingSubmit() {
      try {
        sessionStorage.removeItem(PENDING_SUBMIT_KEY);
      } catch {
        /* ignore storage errors */
      }
    }

    cleanupStalePendingSubmit() {
      const pending = this.readPendingSubmit();
      if (!pending) return;

      const age = Date.now() - (pending.at || 0);
      if (age > PENDING_MAX_AGE_MS) {
        this.clearPendingSubmit();
      }
    }

    markPendingSubmit() {
      this.writePendingSubmit({
        returnPath: this.getReturnPath(),
        at: Date.now(),
      });
    }

    stripStaleContactPostedUrl() {
      if (this.hasFreshPendingSubmit()) return;

      const url = new URL(window.location.href);
      let changed = false;

      if (url.searchParams.get(CONTACT_POSTED_PARAM) === 'true') {
        url.searchParams.delete(CONTACT_POSTED_PARAM);
        changed = true;
      }

      if (url.hash === '#contact_form') {
        url.hash = '';
        changed = true;
      }

      if (!changed) return;

      history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }

    getPostSubmitState() {
      const pending = this.readPendingSubmit();
      if (!pending || !this.isOnReturnPage(pending)) return null;

      if (this.querySelector('[data-form-error-marker]')) {
        return { type: 'error' };
      }

      if (this.querySelector('[data-form-success-marker]')) {
        return { type: 'success' };
      }

      if (this.hasContactPostedParam()) {
        return { type: 'success' };
      }

      if (this.returnedFromChallenge()) {
        return { type: 'success' };
      }

      return null;
    }

    clearPostSubmitMarkers() {
      this.querySelector('[data-form-success-marker]')?.remove();
      this.querySelector('[data-form-error-marker]')?.remove();
    }

    handlePostSubmitState() {
      const postState = this.getPostSubmitState();

      if (!postState) {
        if (!this.hasFreshPendingSubmit()) {
          this.clearPostSubmitMarkers();
          this.stripStaleContactPostedUrl();
        }
        return;
      }

      this.clearPostSubmitMarkers();
      this.clearPendingSubmit();
      this.captchaState = 'idle';

      if (postState.type === 'success') {
        this.showToast('success');
        return;
      }

      this.showToast('error');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    clearToastTimers() {
      if (this.toastHideTimer) {
        clearTimeout(this.toastHideTimer);
        this.toastHideTimer = null;
      }
      if (this.toastRemoveTimer) {
        clearTimeout(this.toastRemoveTimer);
        this.toastRemoveTimer = null;
      }
    }

    hideToast() {
      if (!this.toastEl) return;

      this.toastEl.classList.remove('is-visible');
      this.toastEl.classList.add('is-leaving');

      this.toastRemoveTimer = setTimeout(() => {
        this.toastEl.classList.remove('is-leaving', 'is-success', 'is-error');
        this.toastEl.hidden = true;
        if (this.toastTextEl) this.toastTextEl.textContent = '';
        this.toastRemoveTimer = null;
      }, TOAST_EXIT_MS);
    }

    showToast(type) {
      if (!this.toastEl || !this.toastTextEl) return;

      const message =
        type === 'success'
          ? this.dataset.toastSuccess
          : this.dataset.toastError;

      if (!message) return;

      this.clearToastTimers();
      this.portalToast();

      this.toastEl.classList.remove('is-leaving', 'is-success', 'is-error');
      this.toastEl.classList.add(type === 'success' ? 'is-success' : 'is-error');
      this.toastTextEl.textContent = message;
      this.toastEl.hidden = false;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.toastEl.classList.add('is-visible');
        });
      });

      this.toastHideTimer = setTimeout(() => {
        this.hideToast();
        this.toastHideTimer = null;
      }, TOAST_DURATION_MS);
    }

    initShopifyCaptcha() {
      if (!this.form) return;
      if (this.captchaState === 'loading' || this.captchaState === 'ready') return;

      const protect = window.Shopify?.captcha?.protect;
      if (typeof protect !== 'function') {
        this.captchaState = 'unavailable';
        return;
      }

      this.captchaState = 'loading';
      protect(this.form, () => {
        this.captchaState = 'ready';
        if (this.pendingCaptchaSubmit) {
          this.submitAfterCaptchaReady();
        }
      });
    }

    submitAfterCaptchaReady() {
      if (!this.form) return;
      this.pendingCaptchaSubmit = false;
      this.setSubmitLoading(false);
      this.isCaptchaResubmit = true;

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    }

    setSubmitLoading(isLoading) {
      if (!this.submitButton) return;
      const label = this.submitButton.querySelector('[data-label]');
      const loading = this.submitButton.querySelector('[data-loading]');
      this.submitButton.disabled = isLoading;
      if (label) label.hidden = isLoading;
      if (loading) loading.hidden = !isLoading;
    }

    canSubmitWithoutCaptchaWait() {
      return this.captchaState === 'ready' || this.captchaState === 'unavailable';
    }

    waitForCaptchaThenSubmit() {
      this.pendingCaptchaSubmit = true;
      this.setSubmitLoading(true);
      this.initShopifyCaptcha();

      if (this.captchaState === 'unavailable') {
        this.submitAfterCaptchaReady();
      }
    }

    onTriggerClick(event) {
      const link = event.target.closest('a[href="#contact-popup"]');
      if (!link) return;
      event.preventDefault();
      this.lastFocus = link;
      this.open();
    }

    open() {
      this.resetFormState();
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.initShopifyCaptcha();
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.setSubmitLoading(false);
      this.pendingCaptchaSubmit = false;
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    resetFormState() {
      if (!this.form) return;

      this.form.hidden = false;
      this.setSubmitLoading(false);
      this.pendingCaptchaSubmit = false;
      this.isCaptchaResubmit = false;
      this.form.querySelectorAll('.is-invalid').forEach((el) =>
        el.classList.remove('is-invalid')
      );
      this.form.querySelectorAll('[data-field-error]').forEach((el) => {
        el.textContent = '';
        el.hidden = true;
      });
    }

    onClose() {
      this.close();
    }

    onKeyDown(event) {
      if (!this.isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        if (this.form) this.form.querySelector('input, textarea, button')?.blur();
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
          this.lastFocus.focus();
        }
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onSubmitButtonClick(event) {
      const isValid = this.validate();
      if (!isValid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        return;
      }

      if (!this.readPendingSubmit()) {
        this.markPendingSubmit();
      }
    }

    onSubmit(event) {
      const isValid = this.validate();
      if (!isValid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        return;
      }

      if (this.isCaptchaResubmit) {
        this.isCaptchaResubmit = false;
        return;
      }

      if (!this.readPendingSubmit()) {
        this.markPendingSubmit();
      }

      if (!this.canSubmitWithoutCaptchaWait()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        this.waitForCaptchaThenSubmit();
      }
    }

    validate() {
      if (!this.form) return false;

      let valid = true;
      const requiredMsg = this.dataset.errorRequired || 'This field is required.';
      const emailMsg = this.dataset.errorEmail || 'Enter a valid email address.';
      const messageMsg =
        this.dataset.errorMessageRequired || 'Please enter your message.';

      const fields = [
        {
          input: this.form.querySelector('#contact-popup-email'),
          check: (el) => {
            const value = el.value.trim();
            if (!value) return { invalid: true, message: requiredMsg };
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!re.test(value)) {
              return { invalid: true, message: emailMsg };
            }
            return { invalid: false };
          },
        },
        {
          input: this.form.querySelector('#contact-popup-message'),
          check: (el) => {
            if (!el.value.trim()) return { invalid: true, message: messageMsg };
            return { invalid: false };
          },
        },
      ];

      fields.forEach(({ input, check }) => {
        if (!input) return;

        const field = input.closest('.contact-popup__field');
        const err = field ? field.querySelector('[data-field-error]') : null;
        const result = check(input);

        if (result.invalid) {
          input.classList.add('is-invalid');
          if (err) {
            err.textContent = result.message;
            err.hidden = false;
          }
          if (valid) {
            input.focus();
            valid = false;
          }
        } else {
          input.classList.remove('is-invalid');
          if (err) err.hidden = true;
        }
      });

      return valid;
    }
  }

  customElements.define('contact-popup', ContactPopup);
})();
